import { Box } from '@mui/material'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import upperFirst from 'lodash/upperFirst'
import { DateTime } from 'luxon'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactImageGalleryItem } from 'react-image-gallery'
import { TableCellProps } from 'react-virtualized'

import api from 'src/api'
import { Offense, Offense_StatusEnum, VehicleEventType } from 'src/api/gen/api'
import { CurrencyKind } from 'src/api/types/units'
import { getOffenseStatusColor } from 'src/commons/constants'
import { enumsTranslationKeys } from 'src/commons/i18nEnums'
import CopIcon from 'src/commons/icons/CopIcon'
import { dateToDayJSLocalized } from 'src/commons/temporalHelpers'
import { CellLink } from 'src/components/Table/Cells'
import { FullExportableTable, ISearchBar } from 'src/components/Table/Templates/FullExportableTable'
import { useAgencySettings, useCurrencyFormatter } from 'src/layout/AgencySettings'
import { TagCell } from 'src/muiv5/components/commons/DataTable/CellRenderer/TagCell'

import MdsOffenseProofDialog from './MdsOffenseProofDialog/MdsOffenseProofDialog'

interface IProps {
  className?: string
  data: Offense[]
  filter?: { icon: any; onClick: (event: React.MouseEvent<HTMLElement>) => void; title: string }
  isLoading?: boolean
  onClickExport?: (event: React.MouseEvent<HTMLElement>) => any
  searchBar?: ISearchBar
  truncated?: boolean
}

const ViolationsTable = (props: IProps) => {
  const { t } = useTranslation()
  const [displayedRowData, setdisplayedRowData] = useState<Offense | undefined>()

  const currencyFormatter = useCurrencyFormatter(CurrencyKind.Global)
  const agencySettings = useAgencySettings()

  /**
   * Return React Image Gallery compatible object from image URL
   * @param imageUrl the URL of the image to convert
   * @returns
   */
  const getGaleryImage = (imageUrl: string): ReactImageGalleryItem => ({
    original: imageUrl,
    thumbnail: imageUrl,
  })

  const handleImagesDownloadClick = async (offense_uid: string) => {
    api.offenses.offensesExportPictures({ offense_uid })
  }

  const handleCloseDialog = () => {
    setdisplayedRowData(undefined)
  }

  const columns = [
    {
      label: upperFirst(t('pricing.provider')),
      width: 100,
      dataKey: 'provider_id',
      cellRenderer: (cellProps: TableCellProps) => {
        const offense = cellProps.rowData as Offense
        return <CellLink url={`/providers/${offense.provider_id}`}>{offense.provider_name}</CellLink>
      },
    },
    {
      label: upperFirst(t('policy.kind')),
      width: 150,
      dataKey: 'policy_type',
      cellRenderer: (cellProps: TableCellProps) => {
        const offense = cellProps.rowData as Offense
        return upperFirst(t(enumsTranslationKeys.PolicyKind[offense.policy_type]))
      },
    },
    {
      label: upperFirst(t('policy')),
      width: 150,
      dataKey: 'policy_name',
      cellRenderer: (cellProps: TableCellProps) => {
        const offense = cellProps.rowData as Offense
        return <CellLink url={`/policy/${offense.policy_id}/details`}>{offense.policy_name}</CellLink>
      },
    },
    {
      label: upperFirst(t('jurisdiction')),
      width: 150,
      dataKey: 'jurisdiction_name',
      cellRenderer: (cellProps: TableCellProps) => {
        const offense = cellProps.rowData as Offense
        return offense.jurisdiction_name ? (
          <Chip
            variant="outlined"
            label={
              <Typography variant="body1" component="span">
                {offense.jurisdiction_name}
              </Typography>
            }
            title={offense.jurisdiction_name}
          />
        ) : (
          t('tables.n/a')
        )
      },
    },
    {
      label: upperFirst(t('pricing.vin')),
      width: 150,
      dataKey: 'vin',
      cellRenderer: (cellProps: TableCellProps) => {
        const offense = cellProps.rowData as Offense
        if (offense.device_id && offense.vin) {
          return <CellLink url={`/vehicles/${offense.device_id}`}>{offense.vin}</CellLink>
        }
        return 'N/A'
      },
    },
    {
      id: 'policy_offense_column',
      label: upperFirst(t('policy.dashboard.offense.status')),
      width: 200,
      dataKey: 'status',
      cellRenderer: (props: TableCellProps) => (
        <TagCell
          icon={props.rowData.status === Offense_StatusEnum.confirmed_by_agent ? <CopIcon /> : undefined}
          label={upperFirst(t(enumsTranslationKeys.OffensesStatus[props.cellData]))}
          color={getOffenseStatusColor(props.cellData)}
          onIconClick={
            [Offense_StatusEnum.confirmed_by_agent, Offense_StatusEnum.invalidated].includes(props.rowData.status)
              ? () => setdisplayedRowData(props.rowData)
              : undefined
          }
          {...props}
        />
      ),
    },
    {
      label: upperFirst(t('pricing.start')),
      width: 150,
      dataKey: 'official_start_date',
      cellRenderer: (cellProps: TableCellProps) => dateToDayJSLocalized(cellProps.cellData).format('L LT'),
    },
    {
      label: upperFirst(t('pricing.end')),
      width: 150,
      dataKey: 'official_end_date',
      cellRenderer: (cellProps: TableCellProps) => {
        const offense = cellProps.rowData as Offense
        return offense.official_end_date
          ? dateToDayJSLocalized(offense.official_end_date).format('L LT')
          : t('policy.dashboard.offense.to_be_confirmed')
      },
    },
    {
      label:
        agencySettings.penalty_currency_code === 'pts'
          ? upperFirst(t('pricing.penalty_score'))
          : upperFirst(t('pricing.gross_amount')),
      width: 150,
      dataKey: 'price',
      cellRenderer: (cellProps: TableCellProps) => {
        const offense = cellProps.rowData as Offense
        if (offense.price === undefined || isNaN(offense.price)) {
          return ''
        }
        return `${currencyFormatter(offense.price?.toFixed(2))}`
      },
    },
    {
      label: upperFirst(t('policy.dashboard.offense.state')),
      width: 150,
      dataKey: 'event',
      cellRenderer: (cellProps: TableCellProps) => {
        const cellData = cellProps.cellData as VehicleEventType
        return cellData?.state ? upperFirst(t(enumsTranslationKeys.VehicleStates[cellData.state])) : t('tables.n/a')
      },
    },
    {
      label: upperFirst(t('policy.dashboard.offense.event')),
      width: 200,
      dataKey: 'event',
      cellRenderer: (cellProps: TableCellProps) => {
        const cellData = cellProps.cellData as VehicleEventType
        return cellData?.event ? upperFirst(t(enumsTranslationKeys.EventType[cellData.event])) : t('tables.n/a')
      },
    },
  ]

  return (
    <Box display="flex" flex={1}>
      <FullExportableTable
        data={props.data}
        className={props.className}
        columns={columns}
        filter={props.filter}
        isLoading={props.isLoading}
        noDataMessage={t('violations.table.noData')}
        onClickExport={props.onClickExport}
        searchBar={props.searchBar}
        truncated={props.truncated}
        dataTestId="violation-list"
      />
      {!!displayedRowData && (
        <MdsOffenseProofDialog
          agentName={displayedRowData.agent_name ?? t('tables.n/a')}
          date={
            displayedRowData.reporting_date_time &&
            DateTime.fromJSDate(new Date(displayedRowData.reporting_date_time)).isValid
              ? displayedRowData.reporting_date_time
              : t('tables.n/a')
          }
          type={displayedRowData.status && upperFirst(t(enumsTranslationKeys.OffensesStatus[displayedRowData.status]))}
          vehicleType={displayedRowData.device_type}
          downloadUrl={displayedRowData.image_download_url}
          images={(displayedRowData.images ?? []).map((i) => getGaleryImage(i))}
          onCloseClick={handleCloseDialog}
          onDownloadClick={() => handleImagesDownloadClick(displayedRowData.id)}
        />
      )}
    </Box>
  )
}

export default ViolationsTable
